import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "../components/sidebarLayout";
import { localizedErrorMessage } from "../utils/italianErrorMessage";
import {
  getIssuedDocuments,
  getIssuedPrepaymentDocuments,
  restoreCongressReceipt,
  restoreVoidedReceipt,
  voidIssuedReceipt,
} from "../APIs/ReceiptsAPI";
import { restoreCongressInvoiceAccount } from "../APIs/InvoicesAPI";
import {
  deductHeliteCharge,
  getHeliteBuono,
  printHeliteBuono,
  restoreHeliteCharge,
  signHeliteBuono,
} from "../APIs/HeliteAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import HeliteBuonoSignatureDialog from "../components/HeliteBuonoSignatureDialog";
import { orderLineTotal } from "../utils/orderLinePricing";
import { getOrderSurchargeTotal } from "../utils/orderAdjustments";

const TAB_NORMAL = "NORMAL";
const TAB_HELITE = "HELITE";
const TAB_PREPAYMENTS = "PREPAYMENTS";

function toInputDate(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
}

function getChargeDebitIds(charge) {
  return String(charge?.heliteDebitId || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function canDeductHeliteCharge(charge) {
  const status = String(charge?.status || "").toUpperCase();
  if (!getChargeDebitIds(charge).length) return false;
  return status === "SUCCESS" || status === "PARTIALLY_REVERSED";
}

function canRestoreHeliteCharges(charges = []) {
  return charges.length > 0 && charges.every((charge) => (
    String(charge?.status || "").toUpperCase() === "REVERSED"
    && !charge?.restoredAt
    && !(Array.isArray(charge?.restoredOrderIds) && charge.restoredOrderIds.length > 0)
  ));
}

function heliteChargeStatusColor(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "SUCCESS") return "success";
  if (normalized === "FAILED") return "error";
  if (normalized === "REVERSED" || normalized === "PARTIALLY_REVERSED") return "warning";
  return "default";
}

function heliteChargeStatusLabel(status, t) {
  const normalized = String(status || "").toUpperCase();
  if (!normalized) return "-";
  const translated = t(`issuedDocuments.chargeStatuses.${normalized}`);
  return translated === `issuedDocuments.chargeStatuses.${normalized}` ? status : translated;
}

function orderStatusLabel(status, t) {
  const normalized = String(status || "").toUpperCase();
  if (!normalized) return "-";
  const translated = t(`orderStatus.${normalized}`);
  return translated === `orderStatus.${normalized}` ? status : translated;
}

const CARD_CIRCUIT_LABELS = {
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  AMEX: "American Express",
  BANCOMAT: "Bancomat",
  MAESTRO: "Maestro",
  NEXI_SI: "Nexi / SI",
  OTHER: "Altro",
};

function paymentLabel(method, t, cardCircuit = null) {
  const labels = {
    CASH: t("issuedDocuments.paymentMethods.cash"),
    CARD: t("issuedDocuments.paymentMethods.card"),
    BANK_TRANSFER: t("issuedDocuments.paymentMethods.bankTransfer"),
    VOUCHER: t("issuedDocuments.paymentMethods.voucher"),
    ROOM_CHARGE: t("issuedDocuments.paymentMethods.roomCharge"),
    INVOICE: t("issuedDocuments.paymentMethods.invoice"),
  };
  const methodLabel = labels[method] || method || "-";
  if (method !== "CARD" || !cardCircuit) return methodLabel;
  return `${methodLabel} - ${CARD_CIRCUIT_LABELS[cardCircuit] || cardCircuit}`;
}

function paymentFallbackLabel(row, tab, t) {
  if (tab === TAB_HELITE || row?.kind === "UNLINKED_HELITE") {
    return t("issuedDocuments.historicalHeliteCharge");
  }
  if (row?.kind === "ISSUED_DOCUMENT") {
    return t("issuedDocuments.documentWithoutLinkedPayment", { defaultValue: "Documento fiscale senza pagamento collegato" });
  }
  return "-";
}

function fiscalLabel(value, t) {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "FISCALE") return t("issuedDocuments.fiscal");
  if (normalized === "CORTESIA") return t("issuedDocuments.courtesy");
  return value ? t("issuedDocuments.fiscal") : t("issuedDocuments.courtesy");
}

function translatedEnumLabel(namespace, value, t) {
  const normalized = String(value || "").toUpperCase();
  if (!normalized) return "-";
  const key = `${namespace}.${normalized}`;
  const translated = t(key);
  return translated === key ? normalized : translated;
}

function getClientName(order, t = null) {
  const client = order?.client;
  if (!client) return "-";
  return [client.name, client.surname].filter(Boolean).join(" ").trim()
    || (t ? t("issuedDocuments.clientFallback", { id: client.id }) : `Cliente #${client.id}`);
}

function getContext(order, t = null) {
  if (order?.table?.name) return order.table.name;
  if (order?.tableId) return t ? t("issuedDocuments.tableFallback", { id: order.tableId }) : `Tavolo ${order.tableId}`;
  return t ? t("issuedDocuments.counter") : "Banco";
}

function getOrderLines(order, t = null) {
  const label = (key, fallback) => (t ? t(key) : fallback);
  const products = (order?.orderProducts || []).map((line) => ({
    id: `product-${line.id}`,
    type: label("issuedDocuments.lineTypes.product", "Prodotto"),
    name: line?.product?.name || label("issuedDocuments.lineTypes.product", "Prodotto"),
    quantity: Number(line.quantity || 0),
    total: orderLineTotal(line, line?.product?.price),
  }));

  const packages = (order?.orderPackages || []).map((line) => ({
    id: `package-${line.id}`,
    type: label("issuedDocuments.lineTypes.package", "Pacchetto"),
    name: line?.package?.name || label("issuedDocuments.lineTypes.package", "Pacchetto"),
    quantity: Number(line.quantity || 0),
    total: orderLineTotal(line, line?.package?.price),
  }));

  const menus = (order?.orderFixedPriceMenus || []).map((line) => {
    const quantity = Number(line.quantity || 0);
    const price = Number(line.finalPrice || 0);
    return {
      id: `fixed-menu-${line.id}`,
      type: label("issuedDocuments.lineTypes.menu", "Menu"),
      name: line.menuNameSnapshot || label("issuedDocuments.lineTypes.menu", "Menu"),
      quantity,
      total: Number((price * quantity).toFixed(2)),
    };
  });

  const surchargeTotal = getOrderSurchargeTotal(order);
  const adjustments = surchargeTotal > 0.009 ? [{
    id: `surcharge-${order?.id || "unknown"}`,
    type: label("issuedDocuments.lineTypes.adjustment", "Rettifica"),
    name: label("issuedDocuments.extraCounter", "Extra banco"),
    quantity: 1,
    total: surchargeTotal,
  }] : [];

  return [...products, ...packages, ...menus, ...adjustments];
}

function getPaymentRowOrders(row) {
  if (Array.isArray(row?.orders) && row.orders.length > 0) {
    return row.orders;
  }
  return row?.order ? [row.order] : [];
}

function getPaymentRowOrderLines(row, t = null) {
  return getPaymentRowOrders(row).flatMap((order) => (
    getOrderLines(order, t).map((line) => ({
      ...line,
      orderId: order?.id,
      rowKey: `order-${order?.id || "unknown"}-${line.id}`,
    }))
  ));
}

function getPaymentRowStatusLabel(row, t) {
  const statuses = [...new Set(
    getPaymentRowOrders(row)
      .map((order) => orderStatusLabel(order?.status, t))
      .filter((status) => status && status !== "-")
  )];
  return statuses.length > 0 ? statuses.join(", ") : orderStatusLabel(row?.order?.status, t);
}

function getPaymentRowReceipts(row) {
  return getPaymentRowOrders(row).flatMap((order) => (
    (order?.receipts || []).map((receipt) => ({
      ...receipt,
      orderId: order?.id,
      rowKey: `order-${order?.id || "unknown"}-receipt-${receipt.id}`,
    }))
  ));
}

function getHeliteServiceChips(charges = []) {
  const byCode = new Map();
  charges.forEach((charge) => {
    const label = [charge?.structCode, charge?.dep, charge?.service].filter(Boolean).join("/");
    if (!label) return;
    const existing = byCode.get(label);
    if (!existing) {
      byCode.set(label, { label, status: charge.status });
      return;
    }
    const existingStatus = String(existing.status || "").toUpperCase();
    const nextStatus = String(charge.status || "").toUpperCase();
    if (nextStatus === "FAILED" || (nextStatus.includes("REVERSED") && existingStatus !== "FAILED")) {
      existing.status = charge.status;
    }
  });
  return [...byCode.values()];
}

function getVisiblePayments(row, tab) {
  const payments = Array.isArray(row?.payments) ? row.payments : [];
  return tab === TAB_HELITE
    ? payments.filter((payment) => payment.method === "ROOM_CHARGE")
    : payments.filter((payment) => payment.method !== "ROOM_CHARGE");
}

function getHeliteRoomHolder(payments = [], charges = []) {
  const source = [
    ...(Array.isArray(payments) ? payments : []),
    ...(Array.isArray(charges) ? charges : []),
  ].find((item) => item?.heliteRoomNumber || item?.heliteHolderName);

  if (!source) return "-";
  return [source.heliteRoomNumber, source.heliteHolderName].filter(Boolean).join(" - ") || "-";
}

function normalizeBuono(buono) {
  if (!buono?.id) return null;
  return {
    ...buono,
    isSigned: Boolean(buono.isSigned || (buono.signedAt && (buono.signatureSizeBytes || buono.signatureMime))),
  };
}

function getPaymentRowBuoni(row) {
  const buoni = [
    normalizeBuono(row?.payment?.heliteBuono),
    ...(row?.payments || []).map((payment) => normalizeBuono(payment?.heliteBuono)),
    ...(row?.charges || []).map((charge) => normalizeBuono(charge?.buono)),
  ].filter(Boolean);
  const byId = new Map();
  buoni.forEach((buono) => byId.set(Number(buono.id), buono));
  return [...byId.values()];
}

function getHeliteBuonoGroupId(row) {
  const buono = getPaymentRowBuoni(row)[0];
  return buono?.id ? Number(buono.id) : null;
}

function groupHeliteRowsByBuono(rows) {
  const grouped = [];
  const byBuonoId = new Map();

  rows.forEach((row) => {
    const buonoId = getHeliteBuonoGroupId(row);
    if (!buonoId) {
      grouped.push(row);
      return;
    }

    const existing = byBuonoId.get(buonoId);
    if (!existing) {
      const next = {
        ...row,
        id: `helite-buono-${buonoId}`,
        groupedByBuono: true,
        payments: row.payment ? [row.payment] : [],
        charges: [...(row.charges || [])],
        orders: row.order ? [row.order] : [],
        orderIds: row.order?.id ? [row.order.id] : [],
        amount: Number(row.amount || 0),
      };
      byBuonoId.set(buonoId, next);
      grouped.push(next);
      return;
    }

    if (row.payment) existing.payments.push(row.payment);
    existing.charges.push(...(row.charges || []));
    if (row.order?.id && !existing.orderIds.includes(row.order.id)) {
      existing.orderIds.push(row.order.id);
      existing.orders.push(row.order);
    }
    existing.amount = Number((Number(existing.amount || 0) + Number(row.amount || 0)).toFixed(2));
    if (row.date && (!existing.date || new Date(row.date) < new Date(existing.date))) {
      existing.date = row.date;
    }
  });

  return grouped;
}

function buildDocumentRowsForNormalTab(items) {
  const rows = [];

  (Array.isArray(items) ? items : []).forEach((group) => {
    const order = group?.order || {};
    const payments = getVisiblePayments(group, TAB_NORMAL);
    const receipts = Array.isArray(order?.receipts) ? order.receipts : [];

    if (group?.kind === "ISSUED_DOCUMENT") {
      rows.push({
        ...group,
        id: group.id || `document-${group.document?.id}`,
        group,
        order,
        payments: group.payments || [],
        charges: [],
        isFirstOfOrder: true,
      });
      return;
    }

    if (receipts.length > 0) {
      receipts.forEach((receipt, index) => {
        const payment = payments[index] || payments[0] || null;
        rows.push({
          id: `document-${receipt.id}`,
          kind: "ISSUED_DOCUMENT",
          group,
          order,
          payment,
          payments: payment ? [payment] : [],
          charges: [],
          amount: Number(payment?.amount ?? receipt?.amount ?? 0),
          date: receipt?.printedAt || receipt?.createdAt || payment?.createdAt || order.closedAt || order.createdAt,
          document: {
            id: receipt.id,
            documentNumber: receipt.documentNumber || `#${receipt.id}`,
            type: receipt.type,
          },
          isFirstOfOrder: true,
        });
      });
      return;
    }

    rows.push({
      id: `document-order-${order.id || group?.id || rows.length}`,
      kind: "ISSUED_DOCUMENT",
      group,
      order,
      payment: payments[0] || null,
      payments,
      charges: [],
      amount: Number(payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0).toFixed(2)),
      date: payments[0]?.createdAt || order.closedAt || order.createdAt,
      document: {
        id: null,
        documentNumber: "-",
        type: payments.some((payment) => payment?.isFiscal) ? "FISCALE" : "CORTESIA",
      },
      isFirstOfOrder: true,
    });
  });

  return rows;
}

function buildPaymentRows(items, tab) {
  if (tab !== TAB_HELITE) {
    return buildDocumentRowsForNormalTab(items);
  }

  const rows = [];

  (Array.isArray(items) ? items : []).forEach((group) => {
    const order = group?.order || {};

    if (group?.kind === "ISSUED_DOCUMENT") {
      rows.push({
        ...group,
        id: group.id || `document-${group.document?.id}`,
        group,
        order,
        payments: group.payments || [],
        charges: [],
        isFirstOfOrder: true,
      });
      return;
    }

    getVisiblePayments(group, tab).forEach((payment) => {
      rows.push({
        id: `payment-${payment.id}`,
        kind: "PAYMENT",
        group,
        order,
        payment,
        charges: Array.isArray(payment?.heliteCharges) ? payment.heliteCharges : [],
        amount: Number(payment?.amount || 0),
        date: payment?.createdAt || order.closedAt || order.createdAt,
      });
    });

    if (tab === TAB_HELITE) {
      (Array.isArray(group?.unlinkedHeliteCharges) ? group.unlinkedHeliteCharges : []).forEach((charge) => {
        rows.push({
          id: `unlinked-helite-${charge.id}`,
          kind: "UNLINKED_HELITE",
          group,
          order,
          payment: null,
          charges: [charge],
          amount: Number(charge?.amount || 0),
          date: charge?.createdAt || order.closedAt || order.createdAt,
        });
      });
    }
  });

  const outputRows = tab === TAB_HELITE ? groupHeliteRowsByBuono(rows) : rows;
  let previousOrderId = null;
  return outputRows.map((row) => {
    const orderId = Array.isArray(row.orderIds) && row.orderIds.length > 0
      ? row.orderIds.join(",")
      : row.order?.id ?? row.id;
    const isFirstOfOrder = orderId !== previousOrderId;
    previousOrderId = orderId;
    return { ...row, isFirstOfOrder };
  });
}

function getPrepaymentEventClient(event, t = null) {
  if (!event) return "-";
  const clientName = [event.client?.name, event.client?.surname].filter(Boolean).join(" ").trim();
  return clientName
    || event.company?.companyName
    || event.genericRecipientName
    || event.eventName
    || (t ? t("issuedDocuments.eventFallback", { id: event.id }) : `Evento #${event.id}`);
}

function buildPrepaymentRows(items) {
  return (Array.isArray(items) ? items : []).map((movement) => ({
    id: `prepayment-${movement.id}`,
    kind: "PREPAYMENT",
    movement,
    event: movement.event || movement.reservationEvent || {},
    amount: Number(movement?.amount || 0),
    date: movement?.createdAt || movement?.documentDate,
    isFirstOfOrder: true,
  }));
}

function getPaymentRowClientContext(paymentRow, tab, t) {
  const order = paymentRow?.order || {};
  const completedEvent = getPaymentRowOrders(paymentRow)
    .map((linkedOrder) => linkedOrder?.reservationEvent)
    .find((event) => event?.status === "COMPLETED");
  if (tab === TAB_HELITE) {
    const payments = Array.isArray(paymentRow?.payments) && paymentRow.payments.length > 0
      ? paymentRow.payments
      : paymentRow?.payment ? [paymentRow.payment] : [];
    return {
      primary: getHeliteRoomHolder(payments, paymentRow?.charges || []),
      secondary: [completedEvent?.eventName, getContext(order, t)].filter(Boolean).join(" - "),
      event: completedEvent,
    };
  }
  if (completedEvent) {
    return {
      primary: getPrepaymentEventClient(completedEvent, t),
      secondary: [completedEvent.eventName, getContext(order, t)].filter(Boolean).join(" - "),
      event: completedEvent,
    };
  }

  return {
    primary: getClientName(order, t),
    secondary: getContext(order, t),
  };
}

function buildSearchText(row) {
  const order = row?.order || {};
  const reservationEvent = order?.reservationEvent || {};
  const payments = row?.payments || [];
  const charges = [
    ...payments.flatMap((payment) => payment.heliteCharges || []),
    ...(row?.unlinkedHeliteCharges || []),
  ];

  return [
    order.id,
    order.status,
    row?.document?.documentNumber,
    row?.document?.type,
    getClientName(order),
    getContext(order),
    row?.classification,
    reservationEvent.id,
    reservationEvent.eventName,
    reservationEvent.practiceType,
    reservationEvent.status,
    reservationEvent.genericRecipientName,
    reservationEvent.client?.name,
    reservationEvent.client?.surname,
    reservationEvent.company?.companyName,
    ...payments.flatMap((payment) => [
      payment.id,
      payment.method,
      payment.cardCircuit,
      payment.amount,
      payment.note,
      payment.heliteRoomNumber,
      payment.heliteHolderName,
      payment.heliteBillId,
      payment.heliteBuono?.number,
    ]),
    ...charges.flatMap((charge) => [
      charge.id,
      charge.status,
      charge.structCode,
      charge.dep,
      charge.service,
      charge.heliteRoomNumber,
      charge.heliteHolderName,
      charge.errorMessage,
      charge.heliteDebitId,
      charge.buono?.number,
    ]),
    ...getOrderLines(order).flatMap((line) => [line.type, line.name]),
  ].filter(Boolean).join(" ").toLowerCase();
}

function buildPaymentRowSearchText(paymentRow) {
  if (paymentRow?.kind === "PREPAYMENT") {
    const movement = paymentRow.movement || {};
    const event = paymentRow.event || {};
    return [
      movement.id,
      movement.type,
      movement.operation,
      movement.paymentMethod,
      movement.documentType,
      movement.documentNumber,
      movement.description,
      movement.note,
      movement.amount,
      movement.vatRate,
      event.id,
      event.eventName,
      event.practiceType,
      event.status,
      event.genericRecipientName,
      event.client?.name,
      event.client?.surname,
      event.company?.companyName,
      movement.invoice?.numeroFattura,
      movement.sourceMovement?.documentNumber,
    ].filter(Boolean).join(" ").toLowerCase();
  }

  const groupText = buildSearchText(paymentRow?.group);
  const payment = paymentRow?.payment;
  const charges = paymentRow?.charges || [];
  return [
    groupText,
    paymentRow?.id,
    paymentRow?.amount,
    payment?.id,
    payment?.method,
    payment?.cardCircuit,
    payment?.amount,
    payment?.note,
    payment?.heliteRoomNumber,
    payment?.heliteHolderName,
    payment?.heliteBuono?.number,
    ...charges.flatMap((charge) => [
      charge.id,
      charge.status,
      charge.structCode,
      charge.dep,
      charge.service,
      charge.heliteRoomNumber,
      charge.heliteHolderName,
      charge.errorMessage,
      charge.buono?.number,
    ]),
  ].filter(Boolean).join(" ").toLowerCase();
}

function DetailSection({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function DocumentiEmessiPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = localStorage.getItem("tenantType");
  const { showSnackbar } = useSnackbar();
  const isSaleCongressi = String(tenantType || "").toUpperCase() === "SALE_CONGRESSI";

  const today = useMemo(() => new Date(), []);
  const [tab, setTab] = useState(TAB_NORMAL);
  const [from, setFrom] = useState(toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(toInputDate(today));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ items: [], pagination: { total: 0 } });
  const [selectedRow, setSelectedRow] = useState(null);
  const [buonoDetailsById, setBuonoDetailsById] = useState({});
  const [buonoDetailsLoading, setBuonoDetailsLoading] = useState(false);
  const [buonoToSign, setBuonoToSign] = useState(null);
  const [buonoSignatureSaving, setBuonoSignatureSaving] = useState(false);
  const [printingBuonoIds, setPrintingBuonoIds] = useState(new Set());
  const [deductingChargeIds, setDeductingChargeIds] = useState(new Set());
  const [deductConfirmCharge, setDeductConfirmCharge] = useState(null);
  const [restoreConfirmCharge, setRestoreConfirmCharge] = useState(null);
  const [restoringChargeIds, setRestoringChargeIds] = useState(new Set());
  const [receiptAction, setReceiptAction] = useState(null);
  const [receiptActionSubmitting, setReceiptActionSubmitting] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!token || !tenant) return;
    setLoading(true);
    setError("");
    try {
      const usePrepaymentDocuments = isSaleCongressi && tab === TAB_PREPAYMENTS;
      const loader = usePrepaymentDocuments ? getIssuedPrepaymentDocuments : getIssuedDocuments;
      const result = await loader({
        token,
        tenant,
        tab: usePrepaymentDocuments ? TAB_NORMAL : tab,
        page: page + 1,
        limit: rowsPerPage,
        from,
        to,
      });
      setData({
        items: Array.isArray(result?.items) ? result.items : [],
        pagination: result?.pagination || { total: 0 },
      });
    } catch (err) {
      const message = localizedErrorMessage(err, t("issuedDocuments.loadError"));
      setError(message);
      setData({ items: [], pagination: { total: 0 } });
    } finally {
      setLoading(false);
    }
  }, [from, isSaleCongressi, page, rowsPerPage, tab, tenant, to, token, t]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const paymentRows = useMemo(
    () => isSaleCongressi && tab === TAB_PREPAYMENTS
      ? buildPrepaymentRows(data.items)
      : buildPaymentRows(data.items, tab),
    [data.items, isSaleCongressi, tab]
  );

  useEffect(() => {
    if (!isSaleCongressi && tab === TAB_PREPAYMENTS) {
      setTab(TAB_NORMAL);
    }
  }, [isSaleCongressi, tab]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return paymentRows;
    return paymentRows.filter((row) => buildPaymentRowSearchText(row).includes(q));
  }, [paymentRows, search]);

  const displayedTotal = useMemo(
    () => filteredRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0),
    [filteredRows]
  );

  useEffect(() => {
    if (!selectedRow || tab !== TAB_HELITE) return;
    const buoniToLoad = getPaymentRowBuoni(selectedRow)
      .filter((buono) => buono.isSigned && !buonoDetailsById[buono.id]);
    if (buoniToLoad.length === 0) return;

    let cancelled = false;
    setBuonoDetailsLoading(true);
    Promise.all(buoniToLoad.map((buono) => getHeliteBuono(buono.id, token, tenant).catch(() => null)))
      .then((details) => {
        if (cancelled) return;
        setBuonoDetailsById((prev) => {
          const next = { ...prev };
          details.filter(Boolean).forEach((buono) => {
            next[buono.id] = buono;
          });
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setBuonoDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buonoDetailsById, selectedRow, tab, tenant, token]);

  const handleTabChange = (_, value) => {
    setTab(value);
    setPage(0);
    setSearch("");
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = async () => {
    await loadDocuments();
    showSnackbar(t("issuedDocuments.refreshed"), "success");
  };

  const handleReceiptAction = async () => {
    const documentId = Number(receiptAction?.row?.document?.id);
    if (!documentId || !receiptAction?.type) return;
    setReceiptActionSubmitting(true);
    try {
      if (receiptAction.type === "VOID") {
        await voidIssuedReceipt(documentId, token, tenant);
        setReceiptAction(null);
        await loadDocuments();
        showSnackbar("Documento commerciale annullato sulla cassa fiscale", "success");
        return;
      }

      let restored;
      if (receiptAction.type === "RESTORE_CONGRESS_RECEIPT") {
        restored = await restoreCongressReceipt(documentId, token, tenant);
      } else if (receiptAction.type === "RESTORE_CONGRESS_INVOICE") {
        restored = await restoreCongressInvoiceAccount(documentId, token, tenant);
      } else {
        restored = await restoreVoidedReceipt(documentId, token, tenant);
      }
      const primaryTableId = Number(restored?.primaryTableId);
      const restoredOrderIds = Array.isArray(restored?.restoredOrderIds) ? restored.restoredOrderIds : [];
      const state = {
        restoredOrderIds,
        restoredReservationId: restored?.reservationId || null,
        reservationEventId: restored?.reservationEventId || null,
        checkoutReservationEventId: restored?.reservationEventId || null,
        ...(primaryTableId > 0 ? { checkoutTableId: primaryTableId } : { restoredCounterCheckout: true }),
      };
      setReceiptAction(null);
      navigate(primaryTableId > 0 ? `/punto_cassa?tableId=${primaryTableId}` : "/punto_cassa", { state });
    } catch (err) {
      showSnackbar(err?.message || String(err), "error");
    } finally {
      setReceiptActionSubmitting(false);
    }
  };

  const handleConfirmDeductCharge = async () => {
    const charge = deductConfirmCharge;
    if (!charge || !canDeductHeliteCharge(charge)) return;

    const debitIds = getChargeDebitIds(charge);
    setDeductingChargeIds((prev) => new Set(prev).add(charge.id));
    try {
      for (const debitId of debitIds) {
        await deductHeliteCharge(debitId, token, tenant);
      }
      setDeductConfirmCharge(null);
      setSelectedRow(null);
      await loadDocuments();
      showSnackbar(t("issuedDocuments.heliteDeducted"), "success");
    } catch (err) {
      showSnackbar(err?.message || String(err), "error");
    } finally {
      setDeductingChargeIds((prev) => {
        const next = new Set(prev);
        next.delete(charge.id);
        return next;
      });
    }
  };

  const handleConfirmRestoreCharge = async () => {
    const charge = restoreConfirmCharge;
    if (!charge?.id) return;

    setRestoringChargeIds((prev) => new Set(prev).add(charge.id));
    try {
      const restored = await restoreHeliteCharge(charge.id, token, tenant);
      const primaryTableId = Number(restored?.primaryTableId);
      const restoredOrderIds = Array.isArray(restored?.restoredOrderIds) ? restored.restoredOrderIds : [];
      const state = {
        restoredOrderIds,
        restoredReservationId: restored?.reservationId || null,
        reservationEventId: restored?.reservationEventId || null,
        checkoutReservationEventId: restored?.reservationEventId || null,
        ...(primaryTableId > 0 ? { checkoutTableId: primaryTableId } : { restoredCounterCheckout: true }),
      };
      setRestoreConfirmCharge(null);
      setSelectedRow(null);
      showSnackbar(t("issuedDocuments.heliteRestored"), "success");
      navigate(primaryTableId > 0 ? `/punto_cassa?tableId=${primaryTableId}` : "/punto_cassa", { state });
    } catch (err) {
      showSnackbar(err?.message || String(err), "error");
    } finally {
      setRestoringChargeIds((prev) => {
        const next = new Set(prev);
        next.delete(charge.id);
        return next;
      });
    }
  };

  const handleSaveBuonoSignature = async (signature) => {
    if (!buonoToSign?.id) return;
    setBuonoSignatureSaving(true);
    try {
      const signedBuono = await signHeliteBuono(buonoToSign.id, signature, token, tenant);
      if (signedBuono?.id) {
        setBuonoDetailsById((prev) => ({
          ...prev,
          [signedBuono.id]: normalizeBuono(signedBuono),
        }));
      }
      setBuonoToSign(null);
      await loadDocuments();
      showSnackbar(t("issuedDocuments.buonoSignatureSaved", { defaultValue: "Firma buono salvata" }), "success");
      const shouldPrint = window.confirm(t("issuedDocuments.printBuonoAfterSignature", { defaultValue: "Vuoi stampare ora il buono firmato?" }));
      if (shouldPrint) {
        await handlePrintBuono(signedBuono);
      }
    } catch (err) {
      showSnackbar(err?.message || String(err), "error");
    } finally {
      setBuonoSignatureSaving(false);
    }
  };

  const handlePrintBuono = async (buono) => {
    const buonoId = Number(buono?.id);
    if (!buonoId) return;
    setPrintingBuonoIds((prev) => new Set(prev).add(buonoId));
    try {
      await printHeliteBuono(buonoId, token, tenant);
      showSnackbar(t("issuedDocuments.buonoPrintQueued", { defaultValue: "Stampa buono inviata" }), "success");
    } catch (err) {
      showSnackbar(err?.message || String(err), "error");
    } finally {
      setPrintingBuonoIds((prev) => {
        const next = new Set(prev);
        next.delete(buonoId);
        return next;
      });
    }
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 }, minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", lg: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={950}>
                {t("issuedDocuments.title")}
              </Typography>
              <Typography color="text.secondary">
                {t("issuedDocuments.subtitle")}
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                type="date"
                size="small"
                label={t("issuedDocuments.from")}
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label={t("issuedDocuments.to")}
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {t("issuedDocuments.refresh")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
            sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Tabs value={tab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
              <Tab icon={<CreditCardOutlinedIcon />} iconPosition="start" label={t("issuedDocuments.normalPayments")} value={TAB_NORMAL} />
              <Tab icon={<HotelOutlinedIcon />} iconPosition="start" label={t("issuedDocuments.helitePayments")} value={TAB_HELITE} />
              {isSaleCongressi ? (
                <Tab icon={<ReceiptLongOutlinedIcon />} iconPosition="start" label={t("issuedDocuments.prepaymentDocuments")} value={TAB_PREPAYMENTS} />
              ) : null}
            </Tabs>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <Chip
                color={tab === TAB_HELITE ? "warning" : tab === TAB_PREPAYMENTS ? "info" : "success"}
                variant="outlined"
                label={t("issuedDocuments.countSummary", { count: filteredRows.length, total: formatMoney(displayedTotal) })}
              />
              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("issuedDocuments.searchPlaceholder")}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{ minWidth: { xs: 0, sm: 320 } }}
              />
            </Stack>
          </Stack>

          {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}

          <TableContainer sx={{ maxHeight: "calc(100vh - 330px)", minHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("issuedDocuments.account")}</TableCell>
                  <TableCell>{t("issuedDocuments.date")}</TableCell>
                  <TableCell>{t("issuedDocuments.clientContext")}</TableCell>
                  <TableCell>{tab === TAB_PREPAYMENTS ? t("issuedDocuments.operation") : t("issuedDocuments.payments")}</TableCell>
                  <TableCell>{tab === TAB_HELITE ? t("issuedDocuments.helite") : t("issuedDocuments.document")}</TableCell>
                  <TableCell align="right">{t("issuedDocuments.total")}</TableCell>
                  <TableCell align="right">{t("issuedDocuments.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6 }}>
                      <Alert severity="info">{t("issuedDocuments.noDocuments")}</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => {
                    if (row.kind === "PREPAYMENT") {
                      const movement = row.movement || {};
                      const event = row.event || {};
                      const invoiceNumber = movement.invoice?.numeroFattura;
                      const sourceNumber = movement.sourceMovement?.documentNumber;
                      const documentReference = invoiceNumber
                        ? t("issuedDocuments.invoiceNumber", { number: invoiceNumber })
                        : movement.documentNumber
                          ? movement.documentNumber
                          : sourceNumber
                            ? t("issuedDocuments.sourceDocumentNumber", { number: sourceNumber })
                            : "-";

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography fontWeight={900}>#{event.id || movement.reservationEventId}</Typography>
                            <Chip
                              size="small"
                              label={event.practiceType || t("issuedDocuments.prepaymentDocuments")}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDateTime(row.date)}</TableCell>
                          <TableCell>
                            <Typography fontWeight={800}>{getPrepaymentEventClient(event, t)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {event.eventName || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                color={movement.operation === "RECEIPT" ? "success" : movement.operation === "REFUND" ? "warning" : "error"}
                                label={translatedEnumLabel("issuedDocuments.prepaymentOperations", movement.operation, t)}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={translatedEnumLabel("issuedDocuments.prepaymentTypes", movement.type, t)}
                              />
                              {movement.vatRate !== null && movement.vatRate !== undefined ? (
                                <Chip size="small" variant="outlined" label={`IVA ${movement.vatRate}%`} />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={800}>
                              {translatedEnumLabel("issuedDocuments.prepaymentDocumentTypes", movement.documentType, t)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {documentReference}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={900}>{formatMoney(row.amount)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => setSelectedRow(row)}>
                              {t("issuedDocuments.details")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    const order = row.order || {};
                    const payment = row.payment;
                    const rowPayments = Array.isArray(row.payments) && row.payments.length > 0
                      ? row.payments
                      : payment ? [payment] : [];
                    const group = row.group || {};
                    const charges = row.charges || [];
                    const heliteServiceChips = getHeliteServiceChips(charges);
                    const buoni = getPaymentRowBuoni(row);
                    const receipts = Array.isArray(order.receipts) ? order.receipts : [];
                    const hasFailedCharge = charges.some((charge) => charge.status === "FAILED");
                    const firstDeductableCharge = charges.find(canDeductHeliteCharge);
                    const firstRestorableCharge = canRestoreHeliteCharges(charges) ? charges[0] : null;
                    const clientContext = getPaymentRowClientContext(row, tab, t);
                    const paymentText = rowPayments.length > 1
                      ? `${paymentLabel(rowPayments[0]?.method, t, rowPayments[0]?.cardCircuit)} ${formatMoney(row.amount)}`
                      : rowPayments[0]
                        ? `${paymentLabel(rowPayments[0].method, t, rowPayments[0].cardCircuit)} ${formatMoney(rowPayments[0].amount)}`
                      : paymentFallbackLabel(row, tab, t);
                    const orderLabel = Array.isArray(row.orderIds) && row.orderIds.length > 0
                      ? row.orderIds.map((id) => `#${id}`).join(", ")
                      : `#${order.id}`;
                    const accountLabel = row.document?.documentNumber || orderLabel;
                    const isCommercialReceipt = tab === TAB_NORMAL
                      && row.document?.type === "FISCALE"
                      && !row.invoice
                      && Number(row.document?.id) > 0;
                    const isInvoice = tab === TAB_NORMAL
                      && row.document?.type === "INVOICE"
                      && Boolean(row.invoice)
                      && Number(row.document?.id) > 0;
                    const receiptStatus = String(row.document?.status || "").toUpperCase();
                    const hasCongressPractice = (row.orders || []).some(
                      (linkedOrder) => Number(linkedOrder?.reservationEventId) > 0
                    );
                    const congressRestoreExpired = toInputDate(row.date) !== toInputDate(new Date());
                    const congressRestoreDisabled = receiptActionSubmitting
                      || congressRestoreExpired
                      || !hasCongressPractice
                      || (isCommercialReceipt && !["ISSUED", "VOIDED"].includes(receiptStatus))
                      || (isInvoice && Boolean(row.invoice?.isSent));

                    return (
                      <TableRow
                        key={`${tab}-${row.id}`}
                        hover
                        sx={{
                          "& > td": {
                            borderTop: row.isFirstOfOrder ? "2px solid" : undefined,
                            borderTopColor: row.isFirstOfOrder ? "divider" : undefined,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={900}>{accountLabel}</Typography>
                          {isSaleCongressi && clientContext.event ? (
                            <Chip
                              size="small"
                              color="success"
                              label={`${clientContext.event.eventName} - ${t("completata")}`}
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          ) : null}
                          {row.isFirstOfOrder ? (
                            <Chip size="small" label={group.classification || "-"} variant="outlined" />
                          ) : null}
                        </TableCell>
                        <TableCell>{formatDateTime(row.date)}</TableCell>
                        <TableCell>
                          <Typography fontWeight={800}>{clientContext.primary}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {clientContext.secondary}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              label={paymentText}
                              variant="outlined"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {tab === TAB_HELITE ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {heliteServiceChips.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              ) : heliteServiceChips.map((chip) => (
                                <Chip
                                  key={chip.label}
                                  size="small"
                                  color={heliteChargeStatusColor(chip.status)}
                                  label={chip.label}
                                />
                              ))}
                              {hasFailedCharge ? <Chip size="small" color="error" label={t("issuedDocuments.error")} /> : null}
                              {buoni.map((buono) => {
                                const detail = buonoDetailsById[buono.id];
                                const viewBuono = normalizeBuono({ ...buono, ...(detail || {}) });
                                return (
                                  <Chip
                                    key={buono.id}
                                    size="small"
                                    color={viewBuono.isSigned ? "success" : "warning"}
                                    variant="outlined"
                                    label={`${t("issuedDocuments.buono", { defaultValue: "Buono" })} ${viewBuono.number || `#${viewBuono.id}`} - ${viewBuono.isSigned ? t("issuedDocuments.signed", { defaultValue: "Firmato" }) : t("issuedDocuments.notSigned", { defaultValue: "Non firmato" })}`}
                                  />
                                );
                              })}
                            </Stack>
                          ) : (
                            <Stack spacing={0.25}>
                              <Typography variant="body2" fontWeight={800}>
                                {row.document?.documentNumber || "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.document?.type ? fiscalLabel(row.document.type, t) : (receipts.length ? t("issuedDocuments.documentCount", { count: receipts.length }) : "-")}
                              </Typography>
                              {isCommercialReceipt && receiptStatus !== "ISSUED" ? (
                                <Chip
                                  size="small"
                                  color={receiptStatus === "VOIDED" ? "warning" : receiptStatus === "RESTORED" ? "info" : "default"}
                                  label={receiptStatus}
                                  sx={{ alignSelf: "flex-start" }}
                                />
                              ) : null}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={900}>{formatMoney(row.amount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {isCommercialReceipt && !isSaleCongressi ? (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={receiptStatus !== "ISSUED" || receiptActionSubmitting}
                                onClick={() => setReceiptAction({ type: "VOID", row })}
                              >
                                VOID
                              </Button>
                            ) : null}
                            {isCommercialReceipt && !isSaleCongressi ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                disabled={receiptStatus !== "VOIDED" || receiptActionSubmitting}
                                onClick={() => setReceiptAction({ type: "RESTORE", row })}
                              >
                                RIPRISTINO
                              </Button>
                            ) : null}
                            {isSaleCongressi && (isCommercialReceipt || isInvoice) && hasCongressPractice ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                disabled={congressRestoreDisabled}
                                title={congressRestoreExpired
                                  ? "Il ripristino e disponibile soltanto nel giorno di emissione"
                                  : row.invoice?.isSent
                                    ? "La fattura e gia stata inviata e richiede una nota di credito"
                                    : "Ripristina la pratica in check-in"}
                                onClick={() => setReceiptAction({
                                  type: isInvoice ? "RESTORE_CONGRESS_INVOICE" : "RESTORE_CONGRESS_RECEIPT",
                                  row,
                                })}
                              >
                                RIPRISTINO CONTO
                              </Button>
                            ) : null}
                            {tab === TAB_HELITE ? (
                              <Button
                                size="small"
                                startIcon={<UndoOutlinedIcon />}
                                disabled={!firstDeductableCharge || deductingChargeIds.has(firstDeductableCharge.id)}
                                onClick={() => setDeductConfirmCharge(firstDeductableCharge)}
                              >
                                {t("issuedDocuments.deduct")}
                              </Button>
                            ) : null}
                            {tab === TAB_HELITE ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                startIcon={<ReplayOutlinedIcon />}
                                disabled={!firstRestorableCharge || restoringChargeIds.has(firstRestorableCharge.id)}
                                onClick={() => setRestoreConfirmCharge(firstRestorableCharge)}
                              >
                                {t("issuedDocuments.restore")}
                              </Button>
                            ) : null}
                            <Button size="small" onClick={() => setSelectedRow(row)}>
                              {t("issuedDocuments.details")}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={tab === TAB_HELITE || tab === TAB_PREPAYMENTS
              ? filteredRows.length
              : Number(data?.pagination?.total || filteredRows.length || 0)}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>

        <Dialog open={Boolean(selectedRow)} onClose={() => setSelectedRow(null)} maxWidth="lg" fullWidth>
          <DialogTitle>
            {selectedRow?.kind === "PREPAYMENT"
              ? t("issuedDocuments.prepaymentDetailTitle", { id: selectedRow?.movement?.id })
              : selectedRow?.document?.documentNumber
                ? selectedRow.document.documentNumber
              : selectedRow?.orderIds?.length > 1
                ? t("issuedDocuments.detailTitle", { id: selectedRow.orderIds.join(", ") })
                : t("issuedDocuments.detailTitle", { id: selectedRow?.order?.id })}
          </DialogTitle>
          <DialogContent dividers>
            {selectedRow?.kind === "PREPAYMENT" ? (
              <>
                {(() => {
                  const movement = selectedRow.movement || {};
                  const event = selectedRow.event || {};
                  return (
                    <>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">{t("issuedDocuments.event")}</Typography>
                            <Typography fontWeight={900}>{event.eventName || t("issuedDocuments.eventFallback", { id: event.id || movement.reservationEventId })}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">{t("issuedDocuments.client")}</Typography>
                            <Typography fontWeight={900}>{getPrepaymentEventClient(event, t)}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">{t("issuedDocuments.operation")}</Typography>
                            <Typography fontWeight={900}>{translatedEnumLabel("issuedDocuments.prepaymentOperations", movement.operation, t)}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">{t("issuedDocuments.amount")}</Typography>
                            <Typography fontWeight={900}>{formatMoney(movement.amount)}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <DetailSection title={t("issuedDocuments.prepaymentMovements")}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>{t("issuedDocuments.date")}</TableCell>
                                <TableCell>{t("issuedDocuments.type")}</TableCell>
                                <TableCell>{t("issuedDocuments.method")}</TableCell>
                                <TableCell>{t("issuedDocuments.document")}</TableCell>
                                <TableCell>{t("issuedDocuments.sourceDocument")}</TableCell>
                                <TableCell align="right">{t("issuedDocuments.amount")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>#{movement.id}</TableCell>
                                <TableCell>{formatDateTime(movement.createdAt || movement.documentDate)}</TableCell>
                                <TableCell>
                                  {translatedEnumLabel("issuedDocuments.prepaymentTypes", movement.type, t)}
                                  {movement.vatRate !== null && movement.vatRate !== undefined ? ` - IVA ${movement.vatRate}%` : ""}
                                </TableCell>
                                <TableCell>{paymentLabel(movement.paymentMethod, t)}</TableCell>
                                <TableCell>
                                  {translatedEnumLabel("issuedDocuments.prepaymentDocumentTypes", movement.documentType, t)}
                                  {movement.documentNumber ? ` - ${movement.documentNumber}` : ""}
                                </TableCell>
                                <TableCell>
                                  {movement.sourceMovement?.documentNumber
                                    || (movement.sourceMovement?.id ? `#${movement.sourceMovement.id}` : "-")}
                                </TableCell>
                                <TableCell align="right">{formatMoney(movement.amount)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </DetailSection>

                      {movement.description || movement.note ? (
                        <Alert severity="info">
                          {[movement.description, movement.note].filter(Boolean).join(" - ")}
                        </Alert>
                      ) : null}
                    </>
                  );
                })()}
              </>
            ) : selectedRow ? (
              <>
                {(() => {
                  const detailContext = getPaymentRowClientContext(selectedRow, tab, t);
                  const selectedGroup = selectedRow.group || {};
                  const selectedPaidTotal = tab === TAB_HELITE
                    ? selectedRow.amount
                    : selectedGroup.totals?.paid ?? selectedRow.amount ?? 0;
                  return (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tab === TAB_HELITE ? t("issuedDocuments.roomHolder") : t("issuedDocuments.client")}
                      </Typography>
                      <Typography fontWeight={900}>{detailContext.primary}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">{t("issuedDocuments.context")}</Typography>
                      <Typography fontWeight={900}>{detailContext.secondary}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">{t("issuedDocuments.paidTotal")}</Typography>
                      <Typography fontWeight={900}>{formatMoney(selectedPaidTotal)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">{t("issuedDocuments.orderStatus")}</Typography>
                      <Typography fontWeight={900}>{getPaymentRowStatusLabel(selectedRow, t)}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                  );
                })()}

                <DetailSection title={t("issuedDocuments.payments")}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>{t("issuedDocuments.date")}</TableCell>
                          <TableCell>{t("issuedDocuments.method")}</TableCell>
                          <TableCell>{t("issuedDocuments.type")}</TableCell>
                          <TableCell>{t("issuedDocuments.roomHolder")}</TableCell>
                          <TableCell align="right">{t("issuedDocuments.amount")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(selectedRow.payments) && selectedRow.payments.length > 0
                          ? selectedRow.payments
                          : selectedRow.payment ? [selectedRow.payment] : []).length > 0 ? (
                          (Array.isArray(selectedRow.payments) && selectedRow.payments.length > 0
                            ? selectedRow.payments
                            : [selectedRow.payment]).map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>#{payment.id}</TableCell>
                            <TableCell>{formatDateTime(payment.createdAt)}</TableCell>
                            <TableCell>{paymentLabel(payment.method, t, payment.cardCircuit)}</TableCell>
                            <TableCell>{fiscalLabel(payment.isFiscal, t)}</TableCell>
                            <TableCell>
                              {[payment.heliteRoomNumber, payment.heliteHolderName].filter(Boolean).join(" - ") || "-"}
                            </TableCell>
                            <TableCell align="right">{formatMoney(payment.amount)}</TableCell>
                          </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Alert severity="info">{paymentFallbackLabel(selectedRow, tab, t)}</Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DetailSection>

                <DetailSection title={t("issuedDocuments.helitePayments")}>
                  {(selectedRow.charges || []).length === 0 ? (
                    <Alert severity="info">{t("issuedDocuments.noHeliteCharges")}</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>{t("issuedDocuments.date")}</TableCell>
                            <TableCell>{t("issuedDocuments.codes")}</TableCell>
                            <TableCell>{t("issuedDocuments.roomHolder")}</TableCell>
                            <TableCell>{t("issuedDocuments.status")}</TableCell>
                            <TableCell>{t("issuedDocuments.error")}</TableCell>
                            <TableCell align="right">{t("issuedDocuments.amount")}</TableCell>
                            <TableCell align="right">{t("issuedDocuments.actions")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedRow.charges || []).map((charge) => (
                            <TableRow key={charge.id}>
                              <TableCell>#{charge.id}</TableCell>
                              <TableCell>{formatDateTime(charge.createdAt)}</TableCell>
                              <TableCell>{charge.structCode}/{charge.dep}/{charge.service}</TableCell>
                              <TableCell>
                                {[charge.heliteRoomNumber, charge.heliteHolderName].filter(Boolean).join(" - ") || "-"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={heliteChargeStatusColor(charge.status)}
                                  label={heliteChargeStatusLabel(charge.status, t)}
                                />
                              </TableCell>
                              <TableCell>{charge.errorMessage || "-"}</TableCell>
                              <TableCell align="right">{formatMoney(charge.amount)}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  startIcon={<UndoOutlinedIcon />}
                                  disabled={!canDeductHeliteCharge(charge) || deductingChargeIds.has(charge.id)}
                                  onClick={() => setDeductConfirmCharge(charge)}
                                >
                                  {t("issuedDocuments.deduct")}
                                </Button>
                                {canRestoreHeliteCharges(selectedRow.charges)
                                  && selectedRow.charges[0]?.id === charge.id ? (
                                  <Button
                                    size="small"
                                    color="warning"
                                    startIcon={<ReplayOutlinedIcon />}
                                    disabled={restoringChargeIds.has(charge.id)}
                                    onClick={() => setRestoreConfirmCharge(charge)}
                                  >
                                    {t("issuedDocuments.restore")}
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DetailSection>

                <DetailSection title={t("issuedDocuments.buoni", { defaultValue: "Buoni HElite" })}>
                  {getPaymentRowBuoni(selectedRow).length === 0 ? (
                    <Alert severity="info">{t("issuedDocuments.noBuoni", { defaultValue: "Nessun buono collegato." })}</Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {getPaymentRowBuoni(selectedRow).map((buono) => {
                        const detail = normalizeBuono({ ...buono, ...(buonoDetailsById[buono.id] || {}) });
                        return (
                          <Paper key={buono.id} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography fontWeight={900}>
                                  {t("issuedDocuments.buono", { defaultValue: "Buono" })} {detail.number || `#${detail.id}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  color={detail.isSigned ? "success" : "warning"}
                                  label={detail.isSigned
                                    ? t("issuedDocuments.signed", { defaultValue: "Firmato" })
                                    : t("issuedDocuments.notSigned", { defaultValue: "Non firmato" })}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {formatMoney(detail.amount)}
                                </Typography>
                                {!detail.isSigned ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setBuonoToSign(detail)}
                                  >
                                    {t("issuedDocuments.signBuono", { defaultValue: "Firma" })}
                                  </Button>
                                ) : null}
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!detail.isSigned || printingBuonoIds.has(Number(detail.id))}
                                  onClick={() => handlePrintBuono(detail)}
                                >
                                  {printingBuonoIds.has(Number(detail.id))
                                    ? t("issuedDocuments.printingBuono", { defaultValue: "Stampa..." })
                                    : t("issuedDocuments.printBuono", { defaultValue: "Stampa buono" })}
                                </Button>
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                {[detail.heliteRoomNumber, detail.heliteHolderName].filter(Boolean).join(" - ") || "-"}
                              </Typography>
                              {detail.signatureDataUrl ? (
                                <Box
                                  component="img"
                                  src={detail.signatureDataUrl}
                                  alt={detail.number || "Buono signature"}
                                  sx={{
                                    width: "100%",
                                    maxWidth: 520,
                                    height: "auto",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    bgcolor: "#fff",
                                  }}
                                />
                              ) : detail.isSigned && buonoDetailsLoading ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t("issuedDocuments.loadingSignature", { defaultValue: "Caricamento firma..." })}
                                </Typography>
                              ) : !detail.isSigned ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t("issuedDocuments.buonoNotSigned", { defaultValue: "Buono non firmato." })}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {t("issuedDocuments.signatureNotAvailable", { defaultValue: "Firma non disponibile." })}
                                </Typography>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </DetailSection>

                <DetailSection title={t("issuedDocuments.orderRows")}>
                  {(() => {
                    const orderLines = getPaymentRowOrderLines(selectedRow, t);
                    const showOrderColumn = getPaymentRowOrders(selectedRow).length > 1;
                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {showOrderColumn ? <TableCell>{t("issuedDocuments.account")}</TableCell> : null}
                              <TableCell>{t("issuedDocuments.type")}</TableCell>
                              <TableCell>{t("issuedDocuments.item")}</TableCell>
                              <TableCell align="right">{t("issuedDocuments.quantity")}</TableCell>
                              <TableCell align="right">{t("issuedDocuments.total")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orderLines.map((line) => (
                              <TableRow key={line.rowKey}>
                                {showOrderColumn ? <TableCell>#{line.orderId}</TableCell> : null}
                                <TableCell>{line.type}</TableCell>
                                <TableCell>{line.name}</TableCell>
                                <TableCell align="right">{line.quantity.toLocaleString("it-IT")}</TableCell>
                                <TableCell align="right">{formatMoney(line.total)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </DetailSection>

                <DetailSection title={t("issuedDocuments.commercialDocuments")}>
                  {getPaymentRowReceipts(selectedRow).length === 0 ? (
                    <Alert severity="info">{t("issuedDocuments.noCommercialDocuments")}</Alert>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {getPaymentRowReceipts(selectedRow).map((receipt) => (
                        <Stack key={receipt.rowKey} direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ReceiptLongOutlinedIcon fontSize="small" />
                            <Typography fontWeight={900}>{receipt.documentNumber || t("issuedDocuments.documentNumber", { id: receipt.id })}</Typography>
                            {selectedRow.orderIds?.length > 1 ? (
                              <Chip size="small" variant="outlined" label={`#${receipt.orderId}`} />
                            ) : null}
                            <Chip size="small" label={fiscalLabel(receipt.type, t)} />
                          </Stack>
                          <Typography color="text.secondary">{formatDateTime(receipt.createdAt)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </DetailSection>
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedRow(null)}>{t("issuedDocuments.close")}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(receiptAction)} onClose={() => !receiptActionSubmitting && setReceiptAction(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {receiptAction?.type === "VOID" ? "Annulla documento commerciale" : "Ripristina conto nel Punto Cassa"}
          </DialogTitle>
          <DialogContent>
            <Alert severity={receiptAction?.type === "VOID" ? "warning" : "info"} sx={{ mt: 1 }}>
              {receiptAction?.type === "VOID"
                ? `La cassa fiscale emetterà il documento di annullo per ${receiptAction?.row?.document?.documentNumber || "il documento selezionato"}. L'operazione non è reversibile.`
                : receiptAction?.type === "RESTORE_CONGRESS_RECEIPT"
                  ? "La cassa fiscale emetterà il documento di annullo e la pratica tornerà in Check-in con un nuovo conto contenente tutti i dettagli originali. Questa funzione è disponibile solo oggi."
                  : receiptAction?.type === "RESTORE_CONGRESS_INVOICE"
                    ? `La pratica tornerà in Check-in e il numero ${receiptAction?.row?.document?.documentNumber || "della fattura"} sarà conservato per la riemissione. Questa funzione è disponibile solo oggi.`
                    : "Verrà creato un nuovo conto con tutte le righe originali, il tavolo, il cliente e la prenotazione. Il documento storico resterà invariato."}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReceiptAction(null)} disabled={receiptActionSubmitting}>ANNULLA</Button>
            <Button
              variant="contained"
              color={receiptAction?.type === "VOID" ? "error" : "warning"}
              onClick={handleReceiptAction}
              disabled={receiptActionSubmitting}
              startIcon={receiptActionSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {receiptAction?.type === "VOID" ? "CONFERMA VOID" : "CONFERMA RIPRISTINO"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deductConfirmCharge)} onClose={() => setDeductConfirmCharge(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("issuedDocuments.deductConfirmTitle")}</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 1 }}>
              {t("issuedDocuments.deductConfirmText")}
            </Typography>
            {deductConfirmCharge ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">#{deductConfirmCharge.id}</Typography>
                <Typography variant="body2">{formatMoney(deductConfirmCharge.amount)}</Typography>
                <Typography variant="body2">{getChargeDebitIds(deductConfirmCharge).join(", ")}</Typography>
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeductConfirmCharge(null)}>
              {t("confirmOperation.cancel")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!deductConfirmCharge || deductingChargeIds.has(deductConfirmCharge.id)}
              onClick={handleConfirmDeductCharge}
            >
              {t("issuedDocuments.deduct")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(restoreConfirmCharge)}
          onClose={() => !restoringChargeIds.has(restoreConfirmCharge?.id) && setRestoreConfirmCharge(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("issuedDocuments.restoreHeliteConfirmTitle")}</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              {t("issuedDocuments.restoreHeliteConfirmText")}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRestoreConfirmCharge(null)}
              disabled={restoringChargeIds.has(restoreConfirmCharge?.id)}
            >
              {t("confirmOperation.cancel")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!restoreConfirmCharge || restoringChargeIds.has(restoreConfirmCharge.id)}
              onClick={handleConfirmRestoreCharge}
              startIcon={restoringChargeIds.has(restoreConfirmCharge?.id)
                ? <CircularProgress size={16} color="inherit" />
                : <ReplayOutlinedIcon />}
            >
              {t("issuedDocuments.restore")}
            </Button>
          </DialogActions>
        </Dialog>

        <HeliteBuonoSignatureDialog
          open={Boolean(buonoToSign)}
          buono={buonoToSign}
          saving={buonoSignatureSaving}
          onSkip={() => setBuonoToSign(null)}
          onSave={handleSaveBuonoSignature}
        />
      </Box>
    </SidebarLayout>
  );
}
